import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
  Alert,
  Spinner,
  Modal,
} from "react-bootstrap";
import { Input } from "reactstrap";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import SimpleHeader from "components/Headers/SimpleHeader";
import { extensionForm } from "../configs/Forms/AccountsForms";
import {
  getExtension,
  createExtension,
  createExtensionsBulk,
  updateExtension,
  incrementAlpha,
  generateVoicemailPassword,
} from "./extensionService";
import { serverHost } from "variables/serverdata";

/* -------------------- helpers -------------------- */

function getAuthToken() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return localStorage.getItem("token");
    const parsed = JSON.parse(raw);
    return parsed?.access_token || parsed?.token || parsed?.accessToken || null;
  } catch {
    return localStorage.getItem("token");
  }
}

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const isNumeric = (val) => /^\d+$/.test(val);

const previewExtensions = (base, count) => {
  if (!base || count <= 1) return [];

  // numeric
  if (isNumeric(base)) {
    const start = parseInt(base, 10);
    return Array.from({ length: Math.min(count, 3) }, (_, i) =>
      String(start + i),
    );
  }

  // alphabetic
  const previews = [base];
  let current = base;

  for (let i = 1; i < Math.min(count, 3); i++) {
    current = incrementAlpha(current);
    previews.push(current);
  }

  return previews;
};

/* -------------------- component -------------------- */

const ExtensionAddEdit = () => {
  const { extension_uuid } = useParams();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const editing = Boolean(extension_uuid);

  const [formData, setFormData] = useState({});
  const [advancedData, setAdvancedData] = useState({});
  const initialFormData = useRef({});
  const initialAdvanced = useRef({});

  const [devices, setDevices] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState("");
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [extensionCount, setExtensionCount] = useState(1);
  const [saving, setSaving] = useState(false);
  const [revealedField, setRevealedField] = useState(null);
  const [tollAllowOptions, setTollAllowOptions] = useState([]);

  /* -------------------- init form -------------------- */

  useEffect(() => {
    const init = async () => {
      const defaults = { ...(extensionForm.defaults || {}) };

      Object.keys(extensionForm.labels || {}).forEach((f) => {
        if (!(f in defaults)) defaults[f] = "";
      });

      // split advanced defaults
      const adv = {};
      Object.entries(extensionForm.sections).forEach(([_, sec]) => {
        if (typeof sec === "object" && sec.advanced) {
          sec.fields.forEach((f) => (adv[f] = defaults[f]));
        }
      });

      setFormData(defaults);
      setAdvancedData(adv);
      initialFormData.current = defaults;
      initialAdvanced.current = adv;
    };

    init();
  }, []);

  /* -------------------- load devices -------------------- */

  useEffect(() => {
    const loadDevices = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;

        const site = JSON.parse(localStorage.getItem("selectedSite"));
        const domain_uuid = site?.site_uuid;
        if (!domain_uuid) return;

        const res = await fetch(
          `${serverHost}/get_rows?table=v_devices&domain_uuid=${domain_uuid}&page=1&pageSize=1000`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const data = await res.json();
        if (Array.isArray(data.results)) setDevices(data.results);
      } catch (err) {
        console.error("Failed to load devices", err);
      }
    };

    loadDevices();
  }, []);

  /* -------------------- load extension -------------------- */

  useEffect(() => {
    if (!editing) return;

    const load = async () => {
      try {
        setLoading(true);
        const data = await getExtension(extension_uuid);

        const merged = {
          ...data,
          ...(data.voicemail || {}),
        };

        if (data.device_lines?.length) {
          const line = data.device_lines[0];

          merged.provision_device_uuid = line.device_uuid;
          merged.provision_line = String(line.line_number);
        }

        setFormData((prev) => ({ ...prev, ...merged }));
        initialFormData.current = { ...merged };

        const adv = {};
        Object.entries(extensionForm.sections).forEach(([_, sec]) => {
          if (typeof sec === "object" && sec.advanced) {
            sec.fields.forEach((f) => (adv[f] = merged[f]));
          }
        });

        setAdvancedData(adv);
        initialAdvanced.current = adv;
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [editing, extension_uuid]);

  useEffect(() => {
    const devUuid = formData.provision_device_uuid;
    if (!devUuid || !devices.length) return;

    const device = devices.find((d) => d.device_uuid === devUuid);
    if (!device) return;

    const tpl = device.device_template ?? "";
    if ((formData.provision_template ?? "") === tpl) return;

    setFormData((prev) => ({ ...prev, provision_template: tpl }));
    setAdvancedData((prev) => ({ ...prev, provision_template: tpl }));
  }, [formData.provision_device_uuid, devices]);

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(
          `${serverHost}/get_rows?table=v_domains&page=1&pageSize=1000`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        if (Array.isArray(data.results)) setDomains(data.results);
      } catch (err) {
        console.error("Error fetching domains:", err);
      }
    };
    fetchDomains();
  }, []);

  useEffect(() => {
    if (!domains.length) return;

    setFormData((prev) => {
      if (prev.domain_uuid) return prev;

      const site = JSON.parse(localStorage.getItem("selectedSite"));
      if (!site?.domain_uuid) return prev;

      return {
        ...prev,
        domain_uuid: site.domain_uuid,
      };
    });
  }, [domains]);

  /* -------------------- load group permissions -------------------- */
  useEffect(() => {
    const fetchTollAllowOptions = async () => {
      try {
        const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
        const site_uuid = currentSite?.site_uuid || null;
        const res = await fetch(
          `${serverHost}/get_dialplan_permissions/${site_uuid}`,
        );
        if (!res.ok) throw new Error("Failed to fetch dialplan permissions");
        const data = await res.json();
        const opts = data.data.map((perm) => ({
          label: perm.permission_name,
          value: perm.dialplan_permission_uuid,
        }));
        setTollAllowOptions(opts);
      } catch (err) {
        console.error("Failed to fetch toll_allow options:", err);
      }
    };
    fetchTollAllowOptions();
  }, []);

  /* -------------------- handlers -------------------- */

  const handleChange = (field, value) =>
    setFormData((p) => ({ ...p, [field]: value }));

  const handleAdvancedChange = (field, value) =>
    setAdvancedData((p) => ({ ...p, [field]: value }));

  const handleAdvancedBack = () => {
    const dirty = Object.keys(advancedData).some(
      (k) => advancedData[k] !== initialAdvanced.current[k],
    );
    if (dirty && !window.confirm("Discard advanced changes?")) return;

    setAdvancedData({ ...initialAdvanced.current });
    setShowAdvancedModal(false);
  };

  const handleAdvancedSave = () => {
    setFormData((p) => ({ ...p, ...advancedData }));
    initialAdvanced.current = { ...advancedData };
    setShowAdvancedModal(false);
  };

  /* -------------------- save -------------------- */

  const handleSave = async () => {
    setError("");
    setSaving(true);

    const missing = extensionForm.required.filter(
      (f) => !formData[f] || formData[f].toString().trim() === "",
    );

    if (missing.length) {
      setError(`Missing required fields: ${missing.join(", ")}`);
      return;
    }

    const user = JSON.parse(localStorage.getItem("user"));
    const site = JSON.parse(localStorage.getItem("selectedSite"));
    const client = localStorage.getItem("clientId");

    const hasProvisioning =
      formData.provision_device_uuid &&
      formData.provision_line &&
      String(formData.provision_line).trim() !== "";

    // Auto-generate voicemail password if missing
    let voicemailPassword = formData.voicemail_password;

    if (!voicemailPassword || voicemailPassword.toString().trim() === "") {
      voicemailPassword = generateVoicemailPassword(8);
    }

    const payload = {
      extension: {
        ...formData,
        domain_uuid: site?.site_uuid || null,
        site_uuid: site?.site_uuid || null,
        user_uuid: user?.user_uuid || null,
      },
      voicemail: {
        voicemail_password: voicemailPassword,
        voicemail_enabled: formData.voicemail_enabled,
        voicemail_mail_to: formData.voicemail_mail_to,
        voicemail_file: formData.voicemail_file,
        voicemail_local_after_email: formData.voicemail_local_after_email,
      },
      provisioning: hasProvisioning
        ? {
            device_uuid: formData.provision_device_uuid,
            line_number: String(formData.provision_line),
            template:
              typeof formData.provision_template === "string"
                ? formData.provision_template
                : null,
          }
        : null,
      client_uuid: client || null,
    };

    try {
      if (editing) {
        await updateExtension(extension_uuid, payload);
      } else if (extensionCount > 1) {
        const bulkPayload = {
          start_extension: formData.extension,
          count: extensionCount,
          extension: {
            ...payload.extension,
            extension: formData.extension,
          },
          voicemail: payload.voicemail,
          provisioning: payload.provisioning,
          client_uuid: payload.client_uuid,
        };

        const result = await createExtensionsBulk(bulkPayload);

        if (result.errors_count > 0) {
          console.warn("Bulk create issues:", result);
        }
      } else {
        await createExtension(payload);
      }

      // navigate after success
      navigate("/admin/hucs/extensions");
    } catch (err) {
      setError(err.message || "Failed to save extension");
      setSaving(false);
    }
  };

  /* -------------------- render field -------------------- */

  const renderField = (field, value, onChange) => {
    const type = extensionForm.types?.[field];
    const label = extensionForm.labels?.[field] || titleCase(field);

    const placeholder = value
      ? ""
      : label.toLowerCase() === "extension"
        ? `Enter the alphanumeric ${label.toLowerCase()}`
        : `Enter ${label.toLowerCase()}`;

    // Password-style fields (extension + voicemail)
    if (field === "password" || field === "voicemail_password") {
      const isRevealed = revealedField === field;

      return (
        <Input
          type={isRevealed ? "text" : "password"}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onMouseEnter={() => setRevealedField(field)}
          onMouseLeave={() => setRevealedField(null)}
          autoComplete="new-password"
        />
      );
    }

    if (type === "boolean") {
      return (
        <Input
          type="select"
          value={value === true || value === "true" ? "true" : "false"}
          onChange={(e) => onChange(e.target.value === "true")}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </Input>
      );
    }

    if (type === "select") {
      // Domain dropdown
      if (field === "domain_uuid") {
        return (
          <Input
            type="select"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{`Select ${label.toLowerCase()}…`}</option>
            {domains.map((d) => (
              <option key={d.domain_uuid} value={d.domain_uuid}>
                {d.domain_name || d.domain_uuid}
              </option>
            ))}
          </Input>
        );
      }

      // Device provisioning dropdown
      if (field === "provision_device_uuid") {
        return (
          <Input
            type="select"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{`Select ${label.toLowerCase()}…`}</option>
            {devices.map((d) => (
              <option key={d.device_uuid} value={d.device_uuid}>
                {d.device_address || d.device_label || d.device_uuid}
              </option>
            ))}
          </Input>
        );
      }

      //Group Permissions dropdown
      if (field === "dialplan_permissions") {
        return (
          <Input
            type="select"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={tollAllowOptions.length === 0}
          >
            {tollAllowOptions.length === 0 ? (
              <option value="">No permission groups exist</option>
            ) : (
              <>
                <option value="">Select permission…</option>
                {tollAllowOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </>
            )}
          </Input>
        );
      }
      const opts = extensionForm.options?.[field] || [];

      const isGrouped =
        Array.isArray(opts) &&
        opts.length > 0 &&
        typeof opts[0] === "object" &&
        Array.isArray(opts[0].options);

      if (field === "provision_template" && isGrouped) {
        return (
          <Input
            type="select"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{`Select ${label.toLowerCase()}…`}</option>

            {opts.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {(group.options || []).map((item) => {
                  // item can be string or {label,value}
                  const v = typeof item === "string" ? item : item.value;
                  const l = typeof item === "string" ? item : item.label;
                  return (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </Input>
        );
      }

      return (
        <Input
          type="select"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{`Select ${label.toLowerCase()}…`}</option>
          {opts.map((o, i) =>
            typeof o === "object" ? (
              <option key={i} value={o.value}>
                {o.label}
              </option>
            ) : (
              <option key={i} value={o}>
                {o}
              </option>
            ),
          )}
        </Input>
      );
    }

    // Text input
    return (
      <Input
        type="text"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  /* -------------------- section split -------------------- */

  const basicSections = Object.entries(extensionForm.sections).filter(
    ([_, s]) => !s.advanced,
  );
  const advancedSections = Object.entries(extensionForm.sections).filter(
    ([_, s]) => s.advanced,
  );

  /* -------------------- render -------------------- */

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading extension…</p>
      </div>
    );

  return (
    <>
      <SimpleHeader
        name={`${editing ? "Edit" : "Add"} Extension`}
        parentName="Extensions"
      />

      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header className="header-theme">
            <h4 className="mb-0">
              {editing ? "EDIT EXTENSION" : "ADD EXTENSION"}
            </h4>
          </Card.Header>

          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}

            {advancedSections.length > 0 && (
              <Button
                variant="info"
                className="mb-4"
                onClick={() => setShowAdvancedModal(true)}
              >
                ADVANCED SETTINGS
              </Button>
            )}

            {!editing && (
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Number of Extensions</Form.Label>
                    <Form.Control
                      as="select"
                      value={extensionCount}
                      onChange={(e) =>
                        setExtensionCount(Number(e.target.value))
                      }
                    >
                      {/* 1–10 */}
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}

                      {/* every 5 from 15 to 50 */}
                      {[15, 20, 25, 30, 35, 40, 45, 50].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}

                      {/* 75 and 100 */}
                      {[75, 100].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </Form.Control>

                    <Form.Text className="text-muted">
                      {extensionCount === 1 ? (
                        "A single extension will be created using the value entered below."
                      ) : !formData.extension ? (
                        "Enter a starting extension value above to preview how multiple extensions will be named."
                      ) : (
                        <>
                          Multiple extensions will be created as:&nbsp;
                          <strong>
                            {previewExtensions(
                              formData.extension,
                              extensionCount,
                            ).join(", ")}
                            {extensionCount > 3 && ", …"}
                          </strong>
                        </>
                      )}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Form>
              {basicSections.map(([sectionName, sec]) => {
                const fields = Array.isArray(sec) ? sec : sec.fields;
                console.log(sec.colSize)
                return (
                  <div key={sectionName} className="mb-4">
                    <h5
                      className="section-title mb-3"
                      style={{ fontSize: "1.5rem", fontWeight: 700 }}
                    >
                      {sectionName}
                    </h5>

                    <Row>
                      {fields.map((f) => {
                        if (f === "password" && !editing) return null;

                        if (
                          f === "missed_call_email" &&
                          formData.missed_call_app !== "Email"
                        )
                          return null;

                        return (
                          <Col md={sec.colSize ?? 4} key={f}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                {extensionForm.labels?.[f] || titleCase(f)}
                                {extensionForm.required?.includes(f) && (
                                  <span style={{ color: "red" }}> *</span>
                                )}
                              </Form.Label>

                              {renderField(f, formData[f], (v) =>
                                handleChange(f, v),
                              )}
                            </Form.Group>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                );
              })}

              <div className="mt-4 d-flex justify-content-between">
                <Button
                  variant="danger"
                  onClick={() => navigate("/admin/hucs/extensions")}
                >
                  Cancel
                </Button>
                <Button variant="success" onClick={handleSave}>
                  Save
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>

      {/* ---------------- ADVANCED MODAL ---------------- */}

      <Modal show={showAdvancedModal} onHide={handleAdvancedBack} size="lg">
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
          }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            Advanced Options
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            backgroundColor: isDark ? "#1f1f1f" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          {advancedSections.map(([sectionName, sec]) => (
            <Card key={sectionName} className="mb-4">
              <Card.Header className="header-theme">
                <strong>{sectionName}</strong>
              </Card.Header>
              <Card.Body>
                <Row>
                  {sec.fields.map((f) => (
                    <Col md={4} key={f}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {extensionForm.labels?.[f] || titleCase(f)}
                        </Form.Label>
                        {renderField(f, advancedData[f], (v) =>
                          handleAdvancedChange(f, v),
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          ))}
        </Modal.Body>

        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#111827" : "#f8f9fa",
          }}
        >
          <Button variant="danger" onClick={handleAdvancedBack}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleAdvancedSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="mr-2"
                />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {saving && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card className="p-4 text-center">
            <Spinner animation="border" className="mb-3" />
            <h5 className="mb-1">
              {extensionCount > 1
                ? "Creating extensions…"
                : "Saving extension…"}
            </h5>
            <p className="text-muted mb-0">Please don’t close this page.</p>
          </Card>
        </div>
      )}
    </>
  );
};

export default ExtensionAddEdit;
